import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { MsgEditor } from '../MsgEditor/MsgEditor';
import { SetSegmented, SetSwitch } from '../SettingsShell/controls';
import { RmCard } from './EditorShell';
import { POST_OP_SAFETY_CHIP, isRtl, reviewedLine } from '../../platform-languages';
import type { LanguageCode } from '../../platform-languages';
import {
  RM_AUDIENCE_LABEL,
  RM_CHANNEL_ICON,
  RM_CHANNEL_LABEL,
  RM_FIELD_BY_KEY,
} from '../../settings-reminders/types';
import type {
  RmAudience,
  RmChannel,
  RmFieldKey,
  RmPreviewAppointment,
  RmSection,
  RmStep,
  RmToken,
} from '../../settings-reminders/types';
import { RM_MISSING_COUNTS } from '../../settings-reminders/mockData';
import {
  VERSION_LABEL,
  audienceUnavailable,
  bodyFor,
  callFallsBackToText,
  canHaveTwoVersions,
  confirmAddition,
  previewLanguage,
  resolveForAppointment,
  showsConfirmAffordance,
  stepTextCounts,
  stepReaches,
  subjectFor,
  versionForAppointment,
  versionsOf,
  whenExplanation,
  whenLabel,
} from '../../settings-reminders/derive';
import { rmFieldMenu } from '../../settings-reminders/derive';
import { fieldsUsedIn } from '../../msg/tokens';

export interface RmStepEditorProps {
  section: RmSection;
  step: RmStep;
  index: number;
  appointments: RmPreviewAppointment[];
  onChange: (next: RmStep) => void;
  onRemove: () => void;
  onOpenAppointmentReasons?: (() => void) | undefined;
}

type Version = 'not_confirmed' | 'confirmed';

/**
 * One step, edited (§3). Mounted, not a modal — the old build kept the message
 * inside a *Content →* modal that could not show a preview beside it, so nobody
 * could see what they were writing.
 */
export function RmStepEditor({
  section,
  step,
  index,
  appointments,
  onChange,
  onRemove,
  onOpenAppointmentReasons,
}: RmStepEditorProps) {
  const [version, setVersion] = useState<Version>('not_confirmed');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEnglish, setShowEnglish] = useState(false);

  const set = (patch: Partial<RmStep>) => {
    onChange({ ...step, ...patch });
  };
  const versions = versionsOf(step);
  const active: Version = versions.includes(version) ? version : 'not_confirmed';
  const menu = rmFieldMenu(step.channel);
  const noun = section.kind === 'post_op' ? 'Check' : 'Step';

  return (
    <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-5">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-[var(--text-primary)]">
            {noun} {index + 1} · {RM_CHANNEL_LABEL[step.channel]} · {whenLabel(step, section.kind)}
          </div>
          <div className="mt-0.5 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">
            {whenExplanation(step, section.kind)}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <SetSwitch
            label={`${noun} ${String(index + 1)} on`}
            checked={step.on}
            onChange={(on) => {
              set({ on });
            }}
          />
          {section.maxSteps > 1 ? (
            <button
              type="button"
              onClick={onRemove}
              className="text-[12px] font-semibold text-[var(--alert-red)] hover:underline"
            >
              Remove {noun.toLowerCase()}
            </button>
          ) : null}
        </div>
      </div>

      {/* controls */}
      <div className="mt-4 flex flex-wrap items-end gap-6">
        <Labelled label="Channel">
          <SetSegmented
            value={step.channel}
            options={(['text', 'email', 'ai_call'] as RmChannel[]).map((c) => ({
              value: c,
              label: RM_CHANNEL_LABEL[c],
            }))}
            onChange={(channel) => {
              // A call can only ever go to somebody who has not confirmed, and it
              // is always Smart — so switching channel fixes both rather than
              // leaving an impossible combination on screen.
              set({
                channel,
                ...(channel === 'ai_call'
                  ? { audience: 'not_confirmed', mode: 'smart' as const, twoVersions: false }
                  : {}),
              });
            }}
          />
        </Labelled>

        <Labelled label="When">
          <span className="inline-flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              value={step.offset}
              aria-label="How long"
              onChange={(e) => {
                set({ offset: Math.max(0, Number(e.target.value)) });
              }}
              className="h-8 w-[64px] rounded-[9px] border border-[var(--border-default)] px-2 text-center font-[var(--font-mono)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
            />
            {/* days / hours, and nothing else. *Morning of* was a third thing that
                meant a time of day, not an offset, and could not be computed from
                an appointment whose time nobody had looked at yet. */}
            <select
              value={step.unit}
              aria-label="Days or hours"
              onChange={(e) => {
                set({ unit: e.target.value as RmStep['unit'] });
              }}
              className="h-8 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2 text-[12.5px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
            >
              <option value="days">{section.kind === 'before' ? 'days before' : 'days after'}</option>
              <option value="hours">{section.kind === 'before' ? 'hours before' : 'hours after'}</option>
            </select>
          </span>
        </Labelled>

        <Labelled label="Who writes it">
          {step.channel === 'ai_call' ? (
            <span className="inline-flex items-center gap-2">
              <span className="rounded-[7px] bg-[var(--brand-indigo)] px-2.5 py-1 text-[12px] font-semibold text-white">
                Smart
              </span>
              <span className="text-[11.5px] text-[var(--text-muted)]">Calls are always Smart</span>
            </span>
          ) : (
            <SetSegmented
              value={step.mode}
              options={[
                { value: 'template', label: 'Template' },
                { value: 'smart', label: 'Smart' },
              ]}
              onChange={(mode) => {
                set({ mode });
              }}
            />
          )}
        </Labelled>
      </div>

      {/* who gets it */}
      {section.kind === 'before' ? (
        <div className="mt-4 rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3.5">
          <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Who gets it</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(['everyone', 'not_confirmed', 'confirmed'] as RmAudience[]).map((a) => {
              const why = audienceUnavailable(step.channel, a);
              const on = step.audience === a;
              return (
                <button
                  key={a}
                  type="button"
                  disabled={why !== undefined}
                  title={why}
                  onClick={() => {
                    set({ audience: a, ...(a === 'everyone' ? {} : { twoVersions: false }) });
                  }}
                  className={cn(
                    'rounded-[9px] px-3 py-1.5 text-[12.5px] font-semibold',
                    why !== undefined
                      ? 'cursor-not-allowed border border-[var(--border-default)] text-[var(--text-muted)] opacity-60'
                      : on
                        ? 'bg-[var(--brand-indigo)] text-white'
                        : 'border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)]',
                  )}
                >
                  {RM_AUDIENCE_LABEL[a]}
                </button>
              );
            })}
          </div>

          {/* The switch exists ONLY under Everyone. Under the other two the
              audience already answers the question, so a second message would be
              for nobody. */}
          {canHaveTwoVersions(step) ? (
            <div className="mt-3 flex items-start justify-between gap-4 border-t border-dashed border-[var(--border-default)] pt-3">
              <span className="min-w-0">
                <span className="block text-[12.5px] font-semibold text-[var(--text-primary)]">
                  Different message for patients who’ve confirmed
                </span>
                <span className="mt-0.5 block text-[11px] leading-[1.5] text-[var(--text-muted)]">
                  ELVA picks the version when it sends, from the appointment’s status.
                </span>
              </span>
              <SetSwitch
                label="Different message for patients who’ve confirmed"
                checked={step.twoVersions === true}
                onChange={(twoVersions) => {
                  set({
                    twoVersions,
                    // Seed the second version from the first rather than opening an
                    // empty box: the two messages differ by a sentence, not wholly.
                    ...(twoVersions && (step.bodyConfirmed ?? []).length === 0
                      ? { bodyConfirmed: [...(step.body ?? [])] }
                      : {}),
                  });
                }}
              />
            </div>
          ) : null}
          {step.channel === 'ai_call' ? (
            <div className="mt-2 text-[11.5px] text-[var(--text-muted)]">
              ELVA only calls patients who haven’t confirmed.
            </div>
          ) : null}
        </div>
      ) : null}

      {/* the message */}
      <div className="mt-4">
        {step.channel === 'ai_call' ? (
          <CallBody step={step} section={section} menu={menu} onChange={set} onMenuOpenChange={setMenuOpen} />
        ) : step.mode === 'smart' ? (
          <div>
            <FieldLabel>What should it say?</FieldLabel>
            <textarea
              value={step.brief ?? ''}
              onChange={(e) => {
                set({ brief: e.target.value });
              }}
              rows={3}
              placeholder="Tell ELVA what the message is for."
              className="mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[13.5px] leading-[1.7] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
            />
          </div>
        ) : (
          <>
            {/* Two tabs when there are two versions — one editor each, so nobody
                is editing "the message" and wondering which one. */}
            {versions.length > 1 ? (
              <div className="mb-2 inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
                {versions.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setVersion(v);
                    }}
                    className={cn(
                      'rounded-[7px] px-2.5 py-1 text-[12px]',
                      v === active
                        ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]'
                        : 'text-[var(--text-secondary)]',
                    )}
                  >
                    {VERSION_LABEL[v]}
                  </button>
                ))}
              </div>
            ) : null}

            {step.channel === 'email' ? (
              <div className="mb-3">
                <FieldLabel>Subject</FieldLabel>
                <MsgEditor<RmFieldKey>
                  value={subjectFor(step, active)}
                  onChange={(next) => {
                    set(active === 'confirmed' && step.twoVersions === true ? { subjectConfirmed: next } : { subject: next });
                  }}
                  menu={menu}
                  rows={1}
                  ariaLabel="Subject line"
                  placeholder="What they see in their inbox"
                  onMenuOpenChange={setMenuOpen}
                />
              </div>
            ) : null}

            <FieldLabel>Message</FieldLabel>
            <MsgEditor<RmFieldKey>
              value={bodyFor(step, active)}
              onChange={(next) => {
                set(active === 'confirmed' && step.twoVersions === true ? { bodyConfirmed: next } : { body: next });
              }}
              menu={menu}
              rows={4}
              ariaLabel={`Message — ${VERSION_LABEL[active]}`}
              placeholder="What every patient gets, with their own details filled in"
              onMenuOpenChange={setMenuOpen}
            />

            <Counter step={step} version={active} />

            {/* What ELVA adds, so nobody writes it twice. */}
            {showsConfirmAffordance(step, active) && confirmAddition(step.channel) !== undefined ? (
              <div className="mt-2 rounded-[9px] bg-[var(--brand-indigo-tint)] px-3 py-2 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
                {confirmAddition(step.channel)}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* if a detail is missing */}
      <MissingDetails step={step} onChange={set} onOpenAppointmentReasons={onOpenAppointmentReasons} />

      {/* how it reads */}
      <HowItReads
        section={section}
        step={step}
        appointments={appointments}
        showEnglish={showEnglish}
        onToggleLanguage={setShowEnglish}
        menuOpen={menuOpen}
      />
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{children}</div>;
}

/** `148 characters · English 1 text · Farsi 3 texts` (§3). */
function Counter({ step, version }: { step: RmStep; version: Version }) {
  const counts = stepTextCounts(step, version);
  if (counts.length === 0) return null;
  const chars = counts[0]?.chars ?? 0;
  return (
    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11.5px] text-[var(--text-muted)]">
      <span>
        <b className="font-[var(--font-mono)] font-bold text-[var(--text-secondary)]">{chars}</b> characters
      </span>
      {/* One number per language, because one number would be wrong for one of
          them: Farsi fits 70 characters a part, not 160. */}
      {counts.map((c) => (
        <span key={c.code}>
          {c.name} <b className="font-[var(--font-mono)] font-bold text-[var(--text-secondary)]">{c.texts}</b>{' '}
          {c.texts === 1 ? 'text' : 'texts'}
        </span>
      ))}
      <span>“{reviewedLine('opt_out', 'en')}” is added to the end for you.</span>
    </div>
  );
}

/** The AI-call body: brief, must-include with the locked chip, text version. */
function CallBody({
  step,
  section,
  menu,
  onChange,
  onMenuOpenChange,
}: {
  step: RmStep;
  section: RmSection;
  menu: ReturnType<typeof rmFieldMenu>;
  onChange: (patch: Partial<RmStep>) => void;
  onMenuOpenChange: (open: boolean) => void;
}) {
  const postOp = section.kind === 'post_op';
  return (
    <>
      <FieldLabel>What should it say?</FieldLabel>
      <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">
        Tell ELVA what the call is for. It never diagnoses, gives advice, or says something is normal.
      </p>
      <textarea
        value={step.brief ?? ''}
        onChange={(e) => {
          onChange({ brief: e.target.value });
        }}
        rows={3}
        className="mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[13.5px] leading-[1.7] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
      />

      {postOp ? (
        <>
          <div className="mt-3">
            <FieldLabel>It must include</FieldLabel>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Chip>Pain score, 1 to 10</Chip>
              <Chip>What to watch for</Chip>
              {/* Locked, grey, and it cannot be removed. Every post-op message
                  ends with it — that is the point of the safety line. */}
              <Chip locked>{POST_OP_SAFETY_CHIP}</Chip>
            </div>
            <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">
              Set for every post-op check and can’t be removed.
            </div>
          </div>

          <div className="mt-3 rounded-[12px] bg-[#FBF3E4] px-3.5 py-2.5">
            <div className="text-[12px] font-bold text-[var(--warn-amber)]">If nobody answers</div>
            <div className="mt-0.5 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              The text version below is sent straight away.{' '}
              <b className="font-semibold">ELVA never calls twice for a post-op check.</b>
            </div>
          </div>

          <div className="mt-3">
            <FieldLabel>Text version</FieldLabel>
            <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">Sent when nobody answers.</div>
            <div className="mt-1.5">
              <MsgEditor<RmFieldKey>
                value={step.textVersion ?? []}
                onChange={(textVersion) => {
                  onChange({ textVersion });
                }}
                menu={menu}
                rows={3}
                ariaLabel="Text version"
                placeholder="What goes out if the call isn’t answered"
                onMenuOpenChange={onMenuOpenChange}
              />
            </div>
            <div className="mt-1.5 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              “{POST_OP_SAFETY_CHIP.replace(/[“”]/g, '')}” is added to the end for you. Also sent if ELVA can’t place
              the call, or can’t call in the patient’s language.
            </div>
          </div>
        </>
      ) : (
        <div className="mt-3">
          <FieldLabel>It must include</FieldLabel>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {(step.mustInclude ?? []).map((k) => (
              <Chip key={k}>{RM_FIELD_BY_KEY[k as RmFieldKey].label}</Chip>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Chip({ children, locked }: { children: React.ReactNode; locked?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[7px] px-2 py-1 text-[11.5px] font-semibold',
        locked === true
          ? 'bg-[#F1F3F9] text-[var(--text-muted)]'
          : 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]',
      )}
    >
      {locked === true ? <Icon kind="lock" size={10} stroke={2.2} /> : null}
      {children}
    </span>
  );
}

/** *If a detail is missing* — a fallback per field, and how many patients need it. */
function MissingDetails({
  step,
  onChange,
  onOpenAppointmentReasons,
}: {
  step: RmStep;
  onChange: (patch: Partial<RmStep>) => void;
  onOpenAppointmentReasons?: (() => void) | undefined;
}) {
  const used = fieldsUsedIn<RmFieldKey>(step.body, step.bodyConfirmed, step.subject, step.textVersion);
  const rows = used.filter((k) => RM_FIELD_BY_KEY[k].mayBeEmpty);
  if (rows.length === 0) return null;
  return (
    <RmCard title="If a detail is missing" tone="plain">
      <div className="mt-2 flex flex-col gap-2">
        {rows.map((key) => {
          const def = RM_FIELD_BY_KEY[key];
          const missing = RM_MISSING_COUNTS[key];
          // Visit instructions are not a fallback question: plenty of appointment
          // reasons have none, and the sentence is simply left out.
          if (key === 'visit_instructions') {
            return (
              <div key={key} className="flex flex-wrap items-baseline gap-2 text-[12px]">
                <span className="font-semibold text-[var(--text-primary)]">{def.label}</span>
                <span className="text-[var(--text-muted)]">
                  left out when the appointment type has none — set them on{' '}
                  <button
                    type="button"
                    onClick={onOpenAppointmentReasons}
                    className="font-semibold text-[var(--brand-indigo)] hover:underline"
                  >
                    Appointment reasons
                  </button>
                </span>
              </div>
            );
          }
          return (
            <div key={key} className="flex flex-wrap items-baseline gap-2 text-[12px]">
              <span className="font-semibold text-[var(--text-primary)]">{def.label}</span>
              <span className="text-[var(--text-muted)]">if it’s missing, say</span>
              <input
                value={step.fallbacks?.[key] ?? def.defaultFallback ?? ''}
                aria-label={`Fallback for ${def.label}`}
                onChange={(e) => {
                  onChange({ fallbacks: { ...step.fallbacks, [key]: e.target.value } });
                }}
                className="h-7 w-[140px] rounded-[8px] border border-[var(--border-default)] px-2 text-[12px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
              />
              {missing === undefined ? null : (
                <span className="text-[11px] text-[var(--warn-amber)]">
                  {missing} patients this week have no {def.label.toLowerCase()} on file
                </span>
              )}
            </div>
          );
        })}
      </div>
    </RmCard>
  );
}

/**
 * *How it reads* — **three real appointments this week**, one confirmed, one not,
 * one in another language, each showing the version it will actually get (§3).
 *
 * The switch is `English · Each patient's own`. Without it a practice writing
 * English cannot tell whether the Farsi version exists at all; with only the
 * patient's own, they cannot proofread their own words.
 */
function HowItReads({
  section,
  step,
  appointments,
  showEnglish,
  onToggleLanguage,
  menuOpen,
}: {
  section: RmSection;
  step: RmStep;
  appointments: RmPreviewAppointment[];
  showEnglish: boolean;
  onToggleLanguage: (next: boolean) => void;
  menuOpen: boolean;
}) {
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-[var(--text-primary)]">How it reads</div>
          <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">
            {section.kind === 'post_op'
              ? 'Real patients from this week, each in their language.'
              : 'Three real appointments this week. Each patient sees the version for their status, in their language.'}
          </div>
        </div>
        <div className="inline-flex shrink-0 gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
          {[
            { on: true, label: 'English' },
            { on: false, label: 'Each patient’s own' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                onToggleLanguage(o.on);
              }}
              className={cn(
                'rounded-[7px] px-2.5 py-1 text-[12px]',
                o.on === showEnglish
                  ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]'
                  : 'text-[var(--text-secondary)]',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2.5 grid grid-cols-3 gap-2.5 max-[1100px]:grid-cols-1">
        {appointments.map((appt) => (
          <Reads
            key={appt.id}
            appt={appt}
            step={step}
            section={section}
            showEnglish={showEnglish}
            menuOpen={menuOpen}
          />
        ))}
      </div>
    </div>
  );
}

function Reads({
  appt,
  step,
  section,
  showEnglish,
  menuOpen,
}: {
  appt: RmPreviewAppointment;
  step: RmStep;
  section: RmSection;
  showEnglish: boolean;
  menuOpen: boolean;
}) {
  const language: LanguageCode = previewLanguage(appt, showEnglish);
  const rtl = isRtl(language);
  const version = versionForAppointment(step, appt);
  const reaches = stepReaches(step, appt);
  const callToText = callFallsBackToText(step, language);

  const words = (() => {
    if (step.channel === 'ai_call' && !callToText) {
      return appt.rendered?.spoken ?? 'Add a brief to see how it reads';
    }
    if (language !== 'en' && appt.rendered !== undefined) {
      return version === 'confirmed' ? appt.rendered.confirmed : appt.rendered.notConfirmed;
    }
    const tokens: RmToken[] = callToText ? (step.textVersion ?? []) : bodyFor(step, version);
    return resolveForAppointment(tokens, appt, step.fallbacks).text;
  })();

  return (
    <div className="rounded-[10px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11.5px] font-semibold text-[var(--text-primary)]">{appt.name}</span>
        <span
          className={cn(
            'rounded-[5px] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em]',
            appt.confirmed ? 'bg-[#E8F3EC] text-[var(--money-green)]' : 'bg-[#F1F3F9] text-[var(--text-muted)]',
          )}
        >
          {appt.confirmed ? 'Confirmed' : 'Not confirmed'}
        </span>
      </div>
      <div className="mt-0.5 text-[10.5px] text-[var(--text-muted)]">{appt.when}</div>

      {/* A step this appointment never gets says so, rather than showing a
          message that will not be sent. */}
      {!reaches ? (
        <div className="mt-2 text-[11.5px] italic text-[var(--text-muted)]">
          {step.on
            ? `Doesn’t get this step — it goes to ${step.audience === 'confirmed' ? 'confirmed' : 'not-yet-confirmed'} patients only.`
            : 'This step is switched off.'}
        </div>
      ) : (
        <>
          {/* Farsi lays out right to left. A Farsi message in a left-to-right box
              is not what the patient sees. */}
          <div
            dir={rtl ? 'rtl' : undefined}
            className="mt-1.5 text-[11.5px] leading-[1.7] text-[var(--text-secondary)]"
          >
            {step.channel === 'ai_call' && !callToText ? <span className="text-[var(--text-muted)]">“</span> : null}
            {words}
            {step.channel === 'ai_call' && !callToText ? <span className="text-[var(--text-muted)]">”</span> : null}
            {menuOpen ? (
              <span className="ml-1 rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 text-[10.5px] font-semibold text-[var(--brand-indigo)]">
                choosing a field…
              </span>
            ) : null}
          </div>

          {/* The lines ELVA adds, in the patient's language — our translations,
              not the model's. */}
          {step.channel === 'text' && showsConfirmAffordance(step, version) && section.kind === 'before' ? (
            <div dir={rtl ? 'rtl' : undefined} className="mt-1 text-[11px] text-[var(--text-secondary)]">
              {reviewedLine('confirm_text', language)}
            </div>
          ) : null}
          {step.channel === 'text' ? (
            <div dir={rtl ? 'rtl' : undefined} className="mt-1 text-[10.5px] text-[var(--text-muted)]">
              {reviewedLine('opt_out', language)}
            </div>
          ) : null}
          {section.kind === 'post_op' && step.channel !== 'ai_call' ? (
            <div dir={rtl ? 'rtl' : undefined} className="mt-1 text-[10.5px] text-[var(--text-muted)]">
              {reviewedLine('post_op_safety', language)}
            </div>
          ) : null}
          {/* A language ELVA cannot call in yet: the call is skipped and the text
              goes. Never a silent nothing. */}
          {callToText ? (
            <div className="mt-1.5 text-[11px] text-[var(--warn-amber)]">
              ELVA can’t call in this language yet — the text version goes instead.
            </div>
          ) : null}
          {step.channel === 'email' && showsConfirmAffordance(step, version) && section.kind === 'before' ? (
            <span className="mt-2 inline-flex items-center gap-2">
              <span className="inline-block rounded-[6px] bg-[var(--brand-indigo)] px-2 py-1 text-[10.5px] font-semibold text-white">
                Confirm my appointment
              </span>
              <span className="text-[10.5px] font-semibold text-[var(--brand-indigo)] underline">Change the time</span>
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}

export { RM_CHANNEL_ICON };
export type { IconKind };
